// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  partner/src/pages/Register.jsx
//  Copy to: C:\quickry\partner\src\pages\Register.jsx
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Mail, Lock, ArrowLeft, ChevronRight,
  Check, X, Loader2, RefreshCw, CheckCircle2, Zap,
} from "lucide-react";

const API = typeof window !== "undefined" && window.location.hostname !== "localhost" ? "https://quickry-ivrv.onrender.com/api" : "http://localhost:5000/api";

const C = {
  orange:"#f97316", orangeDark:"#ea580c", orangeLight:"rgba(249,115,22,0.1)",
  black:"#0a0a0a", white:"#ffffff",
  card:"rgba(255,255,255,0.04)", border:"rgba(255,255,255,0.1)",
  borderFocus:"#f97316", text:"rgba(255,255,255,0.85)",
  textSub:"rgba(255,255,255,0.5)", textMuted:"rgba(255,255,255,0.3)",
  red:"#ef4444", green:"#4ade80",
};

// ── OTP Input ─────────────────────────────────────
function OtpInput({ value, onChange, hasError }) {
  const refs  = useRef([]);
  const digits = value.split("").concat(Array(6).fill("")).slice(0, 6);

  const handleKey = (i, e) => {
    if (e.key === "Backspace") {
      onChange(value.slice(0,i) + value.slice(i+1));
      if (i > 0) refs.current[i-1]?.focus();
    } else if (/^\d$/.test(e.key)) {
      const next = (value.slice(0,i) + e.key + value.slice(i+1)).slice(0,6);
      onChange(next);
      if (i < 5) refs.current[i+1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const p = e.clipboardData.getData("text").replace(/\D/g,"").slice(0,6);
    onChange(p);
    refs.current[Math.min(p.length,5)]?.focus();
    e.preventDefault();
  };

  return (
    <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
      {digits.map((d,i) => (
        <input key={i} ref={el=>refs.current[i]=el}
          type="text" inputMode="numeric" maxLength={1} value={d}
          onChange={()=>{}} onKeyDown={e=>handleKey(i,e)}
          onPaste={i===0?handlePaste:undefined} onFocus={e=>e.target.select()}
          style={{ width:48, height:56, borderRadius:12, textAlign:"center", border:`2px solid ${hasError?C.red:d?C.orange:C.border}`, background:d?"rgba(249,115,22,0.08)":C.card, fontSize:"1.4rem", fontWeight:800, color:d?C.orange:C.text, outline:"none", transition:"all .2s", boxShadow:d?"0 2px 10px rgba(249,115,22,0.15)":"none", fontFamily:"inherit" }}/>
      ))}
    </div>
  );
}

// ── Wrap component — OUTSIDE Register function (prevents re-render) ──
function Wrap({ children }) {
  return (
    <div style={{ minHeight:"100vh", background:C.black, fontFamily:"'Plus Jakarta Sans','DM Sans',sans-serif", color:C.white, display:"flex", alignItems:"center", justifyContent:"center", padding:"24px 16px", position:"relative", overflow:"hidden" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,800;9..144,900&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}@keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}@keyframes spin{to{transform:rotate(360deg)}}@keyframes popIn{0%{transform:scale(0.7);opacity:0}100%{transform:scale(1);opacity:1}}.fu{animation:fadeUp .5s ease both}.spin{animation:spin .8s linear infinite}`}</style>
      <div style={{ position:"absolute", top:-100, right:-100, width:400, height:400, borderRadius:"50%", background:"radial-gradient(circle,rgba(249,115,22,0.12),transparent)", pointerEvents:"none" }}/>
      <div style={{ position:"absolute", bottom:-100, left:-100, width:350, height:350, borderRadius:"50%", background:"radial-gradient(circle,rgba(249,115,22,0.08),transparent)", pointerEvents:"none" }}/>
      {children}
    </div>
  );
}

export default function Register() {
  const navigate  = useNavigate();
  const { login, isLoggedIn } = useAuth();

  // If already logged in → dashboard
  useEffect(() => { if (isLoggedIn) navigate("/dashboard"); }, [isLoggedIn]);

  const [screen,   setScreen]   = useState("email"); // email | otp | success
  const [email,    setEmail]    = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [otp,      setOtp]      = useState("");
  const [otpErr,   setOtpErr]   = useState("");
  const [sending,  setSending]  = useState(false);
  const [verifying,setVerifying]= useState(false);
  const [timer,    setTimer]    = useState(30);
  const [canResend,setCanResend]= useState(false);

  // OTP countdown
  useEffect(() => {
    if (screen !== "otp") return;
    setTimer(30); setCanResend(false);
    const iv = setInterval(() => {
      setTimer(t => { if (t<=1) { clearInterval(iv); setCanResend(true); return 0; } return t-1; });
    }, 1000);
    return () => clearInterval(iv);
  }, [screen]);

  // Auto verify
  useEffect(() => { if (otp.length===6) handleVerify(); }, [otp]);

  const validEmail = /^\S+@\S+\.\S+$/.test(email);

  const handleSendOtp = async () => {
    if (!validEmail) { setEmailErr("Valid email address required"); return; }
    setEmailErr(""); setSending(true);
    try {
      // ✅ FIX: /vendors/send-otp use karo — Vendor collection check karega
      // /auth/send-otp galat tha — woh User collection mein save karta tha
      const res  = await fetch(`${API}/vendors/send-otp`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.success) {
        setScreen("otp");
      } else if (res.status === 404) {
        // Vendor registered nahi hai — new registration ke liye onboarding pe bhejo
        // Pehle /auth/send-otp se ek basic session banao taaki email save ho
        const authRes  = await fetch(`${API}/auth/send-otp`, {
          method:"POST", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({ email }),
        });
        const authData = await authRes.json();
        if (authData.success) {
          setScreen("otp");
        } else {
          setEmailErr(authData.message || "Failed to send OTP");
        }
      } else {
        setEmailErr(data.message || "Failed to send OTP");
      }
    } catch(e) {
      setEmailErr("Server se connect nahi ho paya. Backend chal raha hai?");
    } finally { setSending(false); }
  };

  const handleVerify = async () => {
    if (otp.length < 6) return;
    setVerifying(true); setOtpErr("");
    try {
      // ✅ FIX: Pehle /vendors/verify-otp try karo (existing vendor login)
      const vendorRes  = await fetch(`${API}/vendors/verify-otp`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ email, otp }),
      });
      const vendorData = await vendorRes.json();

      if (vendorData.success) {
        // Existing vendor — vendor data ke saath login karo
        login(vendorData.vendor, vendorData.token);
        setScreen("success");
        setTimeout(() => navigate("/dashboard"), 2000);
        return;
      }

      // Vendor nahi mila — new user (just registered) — /auth/verify-otp try karo
      const authRes  = await fetch(`${API}/auth/verify-otp`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ email, otp }),
      });
      const authData = await authRes.json();

      if (authData.success) {
        // Naya user — onboarding form pe bhejo
        login(authData.data || authData.user, authData.token);
        setScreen("success");
        setTimeout(() => navigate("/onboarding"), 2000);
      } else {
        setOtpErr(authData.message || vendorData.message || "Incorrect OTP");
        setOtp("");
      }
    } catch(e) {
      setOtpErr("Server error. Try again!");
      setOtp("");
    } finally { setVerifying(false); }
  };

  const handleResend = () => {
    if (!canResend) return;
    setOtp(""); setOtpErr("");
    setScreen("email");
    setTimeout(() => handleSendOtp(), 100);
  };



  // ── Email Screen ──────────────────────────────
  if (screen === "email") return (
    <Wrap>
      <div style={{ width:"100%", maxWidth:420 }}>
        <button onClick={()=>navigate("/")}
          style={{ display:"flex", alignItems:"center", gap:6, background:"none", border:"none", color:C.textSub, fontSize:".85rem", fontWeight:600, fontFamily:"inherit", marginBottom:28, cursor:"pointer" }}>
          <ArrowLeft size={16}/> Back to home
        </button>

        {/* Logo */}
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width:60, height:60, background:`linear-gradient(135deg,${C.orangeDark},${C.orange})`, borderRadius:18, marginBottom:14, boxShadow:"0 8px 28px rgba(249,115,22,0.4)" }}>
            <Zap size={28} color="#fff" strokeWidth={2.5}/>
          </div>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.7rem", letterSpacing:"-1px" }}>
            Quick<span style={{ color:C.orange }}>ry</span> <span style={{ fontSize:".7rem", color:C.textMuted, fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:400 }}>Partner</span>
          </div>
        </div>

        <div className="fu" style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:24, padding:"32px 28px", boxShadow:"0 24px 64px rgba(0,0,0,0.4)" }}>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.4rem", letterSpacing:"-0.8px", marginBottom:6 }}>Partner Login / Register</div>
          <div style={{ fontSize:".82rem", color:C.textSub, marginBottom:24, lineHeight:1.6 }}>Email pe OTP aayega — koi password nahi!</div>

          {/* Error */}
          {emailErr && (
            <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.2)", borderRadius:10, padding:"10px 14px", marginBottom:16, display:"flex", alignItems:"center", gap:8, fontSize:".8rem", color:"#f87171" }}>
              <X size={14}/> {emailErr}
            </div>
          )}

          <div style={{ marginBottom:6 }}>
            <div style={{ fontSize:".68rem", fontWeight:700, color:C.textMuted, marginBottom:7, letterSpacing:".5px", textTransform:"uppercase" }}>Email Address</div>
            <div style={{ position:"relative" }}>
              <Mail size={16} color={C.textMuted} style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}/>
              <input type="email" placeholder="business@email.com" value={email}
                onChange={e=>{setEmail(e.target.value);setEmailErr("");}}
                onKeyDown={e=>e.key==="Enter"&&handleSendOtp()}
                style={{ width:"100%", background:"rgba(255,255,255,0.06)", border:`1.5px solid ${emailErr?"#ef4444":C.border}`, borderRadius:12, padding:"12px 14px 12px 42px", fontSize:".9rem", color:C.white, transition:"border-color .2s,box-shadow .2s", outline:"none", fontFamily:"inherit" }}
                onFocus={e=>e.target.style.borderColor=C.orange}
                onBlur={e=>e.target.style.borderColor=emailErr?"#ef4444":C.border}/>
              {validEmail && <Check size={16} color={C.green} style={{ position:"absolute", right:14, top:"50%", transform:"translateY(-50%)" }}/>}
            </div>
          </div>

          <button onClick={handleSendOtp} disabled={sending}
            style={{ width:"100%", marginTop:18, background:validEmail?`linear-gradient(135deg,${C.orangeDark},${C.orange})`:"rgba(255,255,255,0.08)", color:validEmail?"#fff":C.textMuted, border:"none", borderRadius:12, padding:"14px", fontWeight:800, fontSize:".97rem", cursor:validEmail?"pointer":"not-allowed", display:"flex", alignItems:"center", justifyContent:"center", gap:8, boxShadow:validEmail?"0 6px 20px rgba(249,115,22,0.28)":"none", transition:"all .25s", fontFamily:"inherit" }}>
            {sending?<><Loader2 size={18} className="spin"/>Sending OTP...</>:<>Get OTP <ChevronRight size={18}/></>}
          </button>

          <div style={{ fontSize:".7rem", color:C.textMuted, textAlign:"center", marginTop:16, lineHeight:1.6 }}>
            By continuing, you agree to our <span style={{ color:C.orange, cursor:"pointer" }}>Terms</span> & <span style={{ color:C.orange, cursor:"pointer" }}>Privacy Policy</span>
          </div>
        </div>
      </div>
    </Wrap>
  );

  // ── OTP Screen ────────────────────────────────
  if (screen === "otp") return (
    <Wrap>
      <div style={{ width:"100%", maxWidth:420 }}>
        <button onClick={()=>{setScreen("email");setOtp("");setOtpErr("");}}
          style={{ display:"flex", alignItems:"center", gap:6, background:"none", border:"none", color:C.textSub, fontSize:".85rem", fontWeight:600, fontFamily:"inherit", marginBottom:28, cursor:"pointer" }}>
          <ArrowLeft size={16}/> Back
        </button>

        <div className="fu" style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:24, padding:"32px 28px", boxShadow:"0 24px 64px rgba(0,0,0,0.4)" }}>
          <div style={{ width:52, height:52, borderRadius:14, background:C.orangeLight, border:"1.5px solid rgba(249,115,22,0.3)", display:"flex", alignItems:"center", justifyContent:"center", marginBottom:18 }}>
            <Lock size={24} color={C.orange} strokeWidth={1.8}/>
          </div>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.4rem", letterSpacing:"-0.8px", marginBottom:6 }}>Check your email</div>
          <div style={{ fontSize:".82rem", color:C.textSub, marginBottom:28, lineHeight:1.6 }}>
            OTP sent to <strong style={{ color:C.white }}>{email}</strong>
            <button onClick={()=>setScreen("email")} style={{ background:"none", border:"none", color:C.orange, fontWeight:700, fontSize:".78rem", cursor:"pointer", marginLeft:8, fontFamily:"inherit" }}>Change</button>
          </div>

          <OtpInput value={otp} onChange={v=>{setOtp(v);setOtpErr("");}} hasError={!!otpErr}/>

          {otpErr && (
            <div style={{ color:"#f87171", fontSize:".75rem", marginTop:10, fontWeight:600, textAlign:"center", display:"flex", alignItems:"center", justifyContent:"center", gap:4 }}>
              <X size={12}/> {otpErr}
            </div>
          )}

          <button onClick={handleVerify} disabled={otp.length<6||verifying}
            style={{ width:"100%", marginTop:20, background:otp.length===6?`linear-gradient(135deg,${C.orangeDark},${C.orange})`:"rgba(255,255,255,0.08)", color:otp.length===6?"#fff":C.textMuted, border:"none", borderRadius:12, padding:"14px", fontWeight:800, fontSize:".97rem", cursor:otp.length===6&&!verifying?"pointer":"not-allowed", display:"flex", alignItems:"center", justifyContent:"center", gap:8, boxShadow:otp.length===6?"0 6px 20px rgba(249,115,22,0.28)":"none", transition:"all .25s", fontFamily:"inherit" }}>
            {verifying?<><Loader2 size={18} className="spin"/>Verifying...</>:<>Verify & Continue <ChevronRight size={18}/></>}
          </button>

          <div style={{ textAlign:"center", marginTop:20 }}>
            {canResend
              ? <button onClick={handleResend} style={{ background:"none", border:"none", cursor:"pointer", color:C.orange, fontWeight:700, fontSize:".83rem", display:"inline-flex", alignItems:"center", gap:5, fontFamily:"inherit" }}>
                  <RefreshCw size={14}/> Resend OTP
                </button>
              : <div style={{ color:C.textMuted, fontSize:".78rem" }}>
                  Resend in <span style={{ color:C.orange, fontWeight:700 }}>00:{String(timer).padStart(2,"0")}</span>
                </div>
            }
          </div>

          <div style={{ marginTop:16, background:"rgba(255,255,255,0.04)", borderRadius:10, padding:"10px 14px", textAlign:"center" }}>
            <div style={{ fontSize:".7rem", color:C.textSub }}>📧 OTP aapke email inbox mein hai — spam folder bhi check karo</div>
          </div>
        </div>
      </div>
    </Wrap>
  );

  // ── Success Screen ────────────────────────────
  if (screen === "success") return (
    <Wrap>
      <div style={{ width:"100%", maxWidth:380, textAlign:"center" }}>
        <div style={{ width:80, height:80, borderRadius:"50%", background:"rgba(22,163,74,0.15)", border:"2px solid rgba(74,222,128,0.4)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px", animation:"popIn .5s ease" }}>
          <CheckCircle2 size={40} color="#4ade80" strokeWidth={2}/>
        </div>
        <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.7rem", letterSpacing:"-1px", marginBottom:10 }}>Verified! 🎉</div>
        <div style={{ color:C.textSub, fontSize:".88rem", lineHeight:1.7 }}>
          Redirecting to onboarding form...<br/>
          <span style={{ fontSize:".78rem", color:C.textMuted }}>Aapka business setup shuru hoga</span>
        </div>
      </div>
    </Wrap>
  );

  return null;
}